import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { FaCheckCircle, FaExclamationCircle, FaUserCircle, FaTasks } from 'react-icons/fa';

const getFaseClasseDefault = (percentual) => {
  if (percentual >= 70) return 'aprimorado';
  if (percentual >= 40) return 'intermediario';
  if (percentual >= 15) return 'inicial';
  return 'inexpressivo';
};

const getFaseTextoDefault = (percentual) => {
  if (percentual >= 70) return 'Aprimorado';
  if (percentual >= 40) return 'Intermediário';
  if (percentual >= 15) return 'Inicial';
  return 'Inexpressivo';
};

const QuestaoCard = ({
  questao,
  onClick,
  showArticulador = false,
  getFaseClasse = getFaseClasseDefault,
  getFaseTexto = getFaseTextoDefault
}) => {
  const pontuacaoPercentual = Math.round((questao.pontuacao || 0) * 100);
  const classeCor = getFaseClasse(pontuacaoPercentual);

  const handleClick = (e) => {
    if (onClick) onClick(questao.id);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onClick) onClick(questao.id);
    }
  };

  const getPriorityClass = () => {
    if (!questao.priorizacao || questao.priorizacao.trim() === '') return 'none';
    return questao.priorizacao
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, '-');
  };

  const priorityClass = getPriorityClass();

  const statusText = questao.status?.toLowerCase() === 'respondida' ? 'Respondida' : 'Pendente';
  const priorityText = questao.priorizacao || 'Normal';

  return (
    <div
      className={`questao-card modern-list-card ${questao.priorizacao ? 'has-priority' : 'no-priority'}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label={`Questão ${questao.codigo}: ${questao.enunciado}. Status: ${statusText}. Prioridade: ${priorityText}`}
    >
      <div className="card-main-layout">
        {/* Barra Lateral de Prioridade */}
        <div className={`priority-sidebar priority-${priorityClass}`}>
          <span className="priority-vertical-text">{questao.priorizacao || ''}</span>
        </div>

        <div className="card-content-wrapper">
          {/* Topo: Eixo > #ID */}
          <div className="card-breadcrumb-header">
            <span className="breadcrumb-eixo">{questao.eixo} &gt; </span>
            <span className="breadcrumb-codigo">#{questao.codigo}</span>
          </div>

          {/* Corpo: Enunciado */}
          <div className="card-body-modern">
            <p className="card-enunciado-serif">{questao.enunciado}</p>
          </div>

          {/* Seção de Progresso */}
          <div className="card-progress-section">
            <label className="progress-label-mini">PROGRESSO</label>
            <div className="progress-flex-container">
              <div className="progress-stats-group">
                <span className={`progress-percent ${classeCor}`}>{pontuacaoPercentual}%</span>
                <span className={`progress-phase ${classeCor}`}>{getFaseTexto(pontuacaoPercentual)}</span>
              </div>
              <div className="progress-bar-track">
                <div className={`progress-bar-thumb ${classeCor}`} style={{ width: `${pontuacaoPercentual}%` }}></div>
              </div>
            </div>
          </div>

          {/* Rodapé: Responsável e Status */}
          <div className="card-footer-modern">
            <div className="footer-badge-item grey" title={`Responsável: ${questao.articuladorNome || 'Não atribuído'}`}>
              <FaUserCircle />
              <span>{questao.articuladorNome ? questao.articuladorNome.split(' ').slice(0, 2).join(' ') : 'Não atribuído'}</span>
            </div>

            {questao.status?.toLowerCase() === 'respondida' ? (
              <div className="footer-badge-item status-badge status-respondida" title="Questão Respondida">
                <FaCheckCircle />
              </div>
            ) : (
              <div className="footer-badge-item status-badge status-pendente" title="Questão Pendente (Não respondida)">
                <FaExclamationCircle />
              </div>
            )}

            {/* Ícone de Plano de Ação */}
            {pontuacaoPercentual < 100 && (
              <div
                className={`footer-badge-item plan-badge ${questao.temPlano ? 'active' : 'warn'}`}
                title={questao.temPlano ? 'Plano de Ação em andamento' : 'Ação abaixo de 100% SEM Plano de Ação definido'}
              >
                <FaTasks />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

QuestaoCard.propTypes = {
  questao: PropTypes.shape({
    id: PropTypes.string.isRequired,
    codigo: PropTypes.string,
    pontuacao: PropTypes.number,
    enunciado: PropTypes.string,
    eixo: PropTypes.string,
    status: PropTypes.string,
    articuladorNome: PropTypes.string,
    priorizacao: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  showArticulador: PropTypes.bool,
  getFaseClasse: PropTypes.func,
  getFaseTexto: PropTypes.func,
};

export default memo(QuestaoCard);
